import { useTranslation } from "react-i18next";
import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Label,
  Input,
  toast,
  cn,
  useAutosizeTextarea,
} from "@cliently/ui";
import { api } from "../lib/api";
import { LottieIllustration } from "../components/LottieIllustration";
import emptyBoxLottie from "../assets/lottie/empty-box.json";

const SUBJECT_MIN = 3;
const SUBJECT_MAX = 120;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
/** Anti double-clic (debounce côté client) */
const SUBMIT_DEBOUNCE_MS = 400;
/** Anti-spam : intervalle minimum entre deux envois effectifs */
const SUBMIT_COOLDOWN_MS = 2500;



const axiosMessage = (err: unknown): string => {
  const e = err as AxiosError<{ message?: string | string[] }>;
  const m = e.response?.data?.message;
  if (Array.isArray(m)) return m.join(", ");
  if (typeof m === "string" && m.length > 0) return m;
  return "Erreur";
}

type RequestType = "DEMANDE" | "RECLAMATION" | "HELP";

const typeLabel: Record<RequestType, string> = {
  DEMANDE: "Demande",
  RECLAMATION: "Réclamation",
  HELP: "Aide",
};

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Comment prendre ou modifier un rendez-vous ?",
    a: "Rendez-vous dans l’onglet « Rendez-vous » : vous pouvez consulter les créneaux proposés et gérer vos réservations selon les règles définies par votre praticien.",
  },
  {
    q: "Comment payer une facture ou voir mes paiements ?",
    a: "L’onglet « Paiements » regroupe vos factures et l’historique des règlements. En cas de question sur un montant, contactez le support via le formulaire ci-dessous.",
  },
  {
    q: "Comment contacter mon praticien ?",
    a: "Utilisez les coordonnées indiquées sur votre espace (carte de contact). Pour toute question d’ordre technique ou générale sur Cliently, utilisez le formulaire « Contacter le support ».",
  },
  {
    q: "Problème de connexion ou bug sur le site ?",
    a: "Décrivez le problème et la date à laquelle il s’est produit dans le formulaire ci-dessous. Un message est également envoyé à l’équipe support (e-mail configuré sur le service).",
  },
];



const statusLabel = (status: string) => {
  switch (status) {
    case "OPEN":
      return "En attente";
    case "IN_PROGRESS":
      return "En cours";
    case "RESOLVED":
      return "Résolue";
    default:
      return status;
  }
}



export const RequestsPage = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: requests, isLoading } = useQuery({
    queryKey: ["portal-requests"],
    queryFn: async () => {
      const { data } = await api.get("/portal/requests");
      return data as Array<{
        id: string;
        type: RequestType;
        subject: string;
        message: string;
        status: string;
        responseMessage: string | null;
        createdAt: string;
      }>;
    },
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { subject: string; message: string }) => {
      const { data } = await api.post("/portal/requests", {
        type: "HELP" as const,
        subject: payload.subject,
        message: payload.message,
      });
      return data;
    },
    onSuccess: () => {
      toast.success(t("requests.helpSent", "Demande d'aide envoyée"));
      qc.invalidateQueries({ queryKey: ["portal-requests"] });
      setFormSubject("");
      setFormMessage("");
    },
    onError: (e) => {
      toast.error(axiosMessage(e));
    },
  });

  const [formSubject, setFormSubject] = useState<string>("");
  const [formMessage, setFormMessage] = useState<string>("");
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const lastSubmitAt = useRef(0);
  const lastClickAt = useRef(0);

  useAutosizeTextarea(messageRef, formMessage, {
    minHeightPx: 120,
    maxHeightPx: 560,
  });

  const messageLen = formMessage.length;
  const subjectTrim = formSubject.trim();
  const messageTrim = formMessage.trim();
  const messageTooShort =
    messageTrim.length > 0 && messageTrim.length < MESSAGE_MIN;
  const canSendFormat =
    subjectTrim.length >= SUBJECT_MIN &&
    subjectTrim.length <= SUBJECT_MAX &&
    messageTrim.length >= MESSAGE_MIN &&
    messageTrim.length <= MESSAGE_MAX;

  const handleSend = useCallback(() => {
    const nowClick = Date.now();
    if (nowClick - lastClickAt.current < SUBMIT_DEBOUNCE_MS) {
      return;
    }
    lastClickAt.current = nowClick;

    if (!canSendFormat) {
      if (
        subjectTrim.length < SUBJECT_MIN ||
        subjectTrim.length > SUBJECT_MAX
      ) {
        toast.error(
          t(
            "requests.subjectLength",
            "L'objet doit contenir entre {{min}} et {{max}} caractères.",
            { min: SUBJECT_MIN, max: SUBJECT_MAX },
          ),
        );
      } else if (messageTrim.length < MESSAGE_MIN) {
        toast.error(
          t(
            "requests.messageTooShort",
            "Le message doit contenir au moins {{min}} caractères.",
            { min: MESSAGE_MIN },
          ),
        );
      } else if (messageTrim.length > MESSAGE_MAX) {
        toast.error(
          t(
            "requests.messageTooLong",
            "Le message ne peut pas dépasser {{max}} caractères.",
            { max: MESSAGE_MAX },
          ),
        );
      }
      return;
    }

    const now = Date.now();
    if (
      now - lastSubmitAt.current < SUBMIT_COOLDOWN_MS &&
      lastSubmitAt.current > 0
    ) {
      toast.error(
        t(
          "requests.submitCooldown",
          "Veuillez patienter quelques secondes avant d'envoyer à nouveau.",
        ),
      );
      return;
    }
    lastSubmitAt.current = now;
    createMutation.mutate({
      subject: subjectTrim,
      message: messageTrim,
    });
  }, [canSendFormat, createMutation, messageTrim, subjectTrim, t]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("requests.title", "Aide")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "requests.subtitle",
            "Consultez la FAQ ou écrivez au support : votre message est transmis à l’équipe (notification par e-mail).",
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {t("requests.faqTitle", "Questions fréquentes")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {FAQ_ITEMS.map((item) => (
            <details
              key={item.q}
              className="border-input group rounded-lg border px-3 py-2 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer list-none text-sm font-medium">
                {item.q}
              </summary>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                {item.a}
              </p>
            </details>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t("requests.formTitle", "Contacter le support")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="support-subject">{t("requests.subject", "Objet")}</Label>
            <Input
              id="support-subject"
              value={formSubject}
              onChange={(e) =>
                setFormSubject(e.target.value.slice(0, SUBJECT_MAX))
              }
              maxLength={SUBJECT_MAX}
              placeholder={t(
                "requests.subjectPlaceholder",
                "Ex : question sur une facture",
              )}
              disabled={createMutation.isPending}
              aria-invalid={
                subjectTrim.length > 0 &&
                (subjectTrim.length < SUBJECT_MIN ||
                  subjectTrim.length > SUBJECT_MAX)
              }
            />
            <p className="text-muted-foreground text-xs">
              {t("requests.subjectCounter", "{{current}} / {{max}}", {
                current: formSubject.length,
                max: SUBJECT_MAX,
              })}
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="support-message">{t("requests.message", "Message")}</Label>
            <textarea
              id="support-message"
              ref={messageRef}
              rows={3}
              maxLength={MESSAGE_MAX}
              className={cn(
                "border-input bg-background focus-visible:ring-ring flex w-full resize-none rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1",
                messageTooShort && "border-amber-600/60",
              )}
              value={formMessage}
              onChange={(e) =>
                setFormMessage(e.target.value.slice(0, MESSAGE_MAX))
              }
              placeholder={t(
                "requests.messagePlaceholder",
                "Décrivez votre demande d'aide...",
              )}
              disabled={createMutation.isPending}
              aria-invalid={messageTooShort}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <p className="text-muted-foreground">
                {t(
                  "requests.messageRules",
                  "Entre {{min}} et {{max}} caractères.",
                  { min: MESSAGE_MIN, max: MESSAGE_MAX },
                )}
              </p>
              <p
                className={cn(
                  "tabular-nums",
                  messageLen > MESSAGE_MAX
                    ? "text-destructive"
                    : messageTooShort
                      ? "text-amber-600"
                      : "text-muted-foreground",
                )}
              >
                {t("requests.messageCounter", "{{current}} / {{max}}", {
                  current: messageLen,
                  max: MESSAGE_MAX,
                })}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleSend}
              disabled={createMutation.isPending}
            >
              {t("requests.submit", "Envoyer")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t("requests.historyTitle", "Mes demandes d'aide")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="text-muted-foreground">
              {t("common.loading", "Chargement...")}
            </p>
          ) : !requests || requests.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-2 text-center">
              <LottieIllustration
                animationData={emptyBoxLottie}
                size={160}
                className="opacity-90"
              />
              <p>{t("requests.empty", "Aucune demande pour le moment")}</p>
            </div>
          ) : (
            requests.map((r) => (
              <div key={r.id} className="space-y-2 rounded-lg border p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{typeLabel[r.type]}</Badge>
                  <Badge variant="outline">{statusLabel(r.status)}</Badge>
                  <span className="text-muted-foreground text-xs">
                    {new Date(r.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold">{r.subject}</p>
                  <p className="whitespace-pre-wrap text-sm">{r.message}</p>
                  {r.responseMessage && (
                    <div className="pt-2">
                      <p className="text-muted-foreground text-xs">
                        {t("requests.response", "Réponse")}
                      </p>
                      <p className="whitespace-pre-wrap text-sm">
                        {r.responseMessage}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
